/*!
 * SPDX-License-Identifier: AGPL-3.0-only
 * 
 * PV Tool — AGPL Community Edition
 * Based on the last AGPL-3.0 version published on 2026-03-18
 * 
 * Copyright (c) 2026 DanteAlighieri13210914
 * Copyright (c) 2026 Contributors to PV Tool AGPL Community Edition
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, version 3 of the License.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 * 
 * Source repository: https://github.com/yandujun363/pv-tool-agpl
 */

import * as PIXI from 'pixi.js';
import { BaseEffect } from './base';
import type { UpdateContext } from '../core/types';

/**
 * Animated film grain / noise overlay.
 * Regenerates noise pattern every few frames for a living texture feel.
 */
export class FilmGrain extends BaseEffect {
  readonly name = 'filmGrain';
  override readonly heavy = true;
  private tiling!: PIXI.TilingSprite;
  private canvas!: HTMLCanvasElement;
  private ctx2d!: CanvasRenderingContext2D;
  private texture!: PIXI.Texture;
  private tick = 0;

  protected setup(): void {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 512;
    this.canvas.height = 512;
    this.ctx2d = this.canvas.getContext('2d')!;

    this.texture = PIXI.Texture.from(this.canvas);
    this.tiling = new PIXI.TilingSprite({
      texture: this.texture,
      width: 1920,
      height: 1080,
    });
    this.tiling.alpha = this.config.alpha ?? 0.08;
    this.container.addChild(this.tiling);

    this.regenerate();
  }

  private regenerate(): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const imgData = this.ctx2d.createImageData(w, h);
    const data = imgData.data;
    const mono = this.config.mono ?? true;

    for (let i = 0; i < data.length; i += 4) {
      const v = Math.random() * 255;
      data[i] = mono ? v : Math.random() * 255;
      data[i + 1] = mono ? v : Math.random() * 255;
      data[i + 2] = mono ? v : Math.random() * 255;
      data[i + 3] = 255;
    }

    this.ctx2d.putImageData(imgData, 0, 0);
    this.texture.source.update();
  }

  update(ctx: UpdateContext): void {
    this.tick++;
    const interval = this.config.updateInterval ?? 3;
    if (this.tick % interval === 0) {
      this.regenerate();
    }

    this.tiling.width = ctx.screenWidth;
    this.tiling.height = ctx.screenHeight;
    this.tiling.alpha = this.config.alpha ?? 0.08;
  }
}